// usePlayerProfile Hook
// Manages player profile data fetching, caching, and state

import { useState, useEffect, useCallback, useMemo } from 'react';
import playerProfileService from '../../services/playerProfile/playerProfileService';
import { useNotification } from '../../context/NotificationContext';
import { useUser } from '../../context/UserContext';

// Cache for profile data
const profileCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const usePlayerProfile = (userId, options = {}) => {
  const { showError } = useNotification();
  const { user: currentUser, userProfile } = useUser();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allowedSections, setAllowedSections] = useState([]);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedUserId, setFetchedUserId] = useState(null);

  // Reset state immediately when userId changes to prevent stale data
  const [prevUserId, setPrevUserId] = useState(userId);
  if (userId !== prevUserId) {
    setPrevUserId(userId);
    setProfile(null);
    setProfileNotFound(false);
    setError(null);
    setFetchedUserId(null);
    if (userId) {
      setLoading(true);
    }
  }

  const {
    sections = 'all',
    includeAnalytics = false,
    skipCache = false,
    previewMode = 'owner'
  } = options;

  // Check if current user is the profile owner
  useEffect(() => {
    if (userId) {
      // Check multiple ID fields for compatibility
      const isOwnerCheck = 
        (userProfile && userProfile._id === userId) ||    // MongoDB _id from backend
        (currentUser && currentUser.uid === userId);       // Firebase UID fallback
      
      setIsOwner(isOwnerCheck);
    }
  }, [currentUser, userProfile, userId]);

  // Get cache key
  const cacheKey = useMemo(() => 
    `${userId}-${sections}-${includeAnalytics}`,
    [userId, sections, includeAnalytics]
  );

  // Fetch profile data
  const fetchProfile = useCallback(async (useCache = true) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Check if current user is the profile owner
      const isOwnerCheck = 
        (userProfile && userProfile._id === userId) ||
        (currentUser && currentUser.uid === userId);

      // Check cache first
      if (useCache && !skipCache && profileCache.has(cacheKey)) {
        const cached = profileCache.get(cacheKey);
        if (Date.now() - cached.timestamp < CACHE_DURATION) {
          setProfile(cached.data.profile);
          setAllowedSections(cached.data.allowedSections);
          setLoading(false);
          return cached.data;
        }
      }

      // Fetch from API
      const response = await playerProfileService.getEnhancedProfile(userId, {
        sections,
        includeAnalytics: includeAnalytics && isOwner
      });

      // Update state
      setProfile(response.profile);
      setAllowedSections(response.allowedSections || []);

      // Update cache
      profileCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });

      return response;
    } catch (err) {
      console.error('Error fetching profile:', err);
      
      // Check if current user is viewing their own profile for better error messaging
      const isViewingOwnProfile = 
        (userProfile && userProfile._id === userId) ||
        (currentUser && currentUser.uid === userId);
      
      // Check error type
      if (err.message === 'USER_NOT_FOUND') {
        // User doesn't exist in backend - this is common for new users
        setProfileNotFound(true);
        setError('Profile not found');
        // For owners, we'll allow profile creation anyway
        if (!isOwner && !isViewingOwnProfile) {
          showError('User account not found.');
        }
      } else if (err.message === 'PROFILE_NOT_FOUND' || err.message.includes('Profile not found')) {
        // Profile doesn't exist but user does - normal for new profiles
        setProfileNotFound(true);
        setError('Profile not found');
        // SUPPRESSED: Don't show error notification when profile simply doesn't exist yet
        // This is expected for new users and they'll see the create profile page
      } else {
        setError(err.message);
        showError(`Failed to load profile: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setFetchedUserId(userId);
    }
  }, [userId, sections, includeAnalytics, isOwner, skipCache, cacheKey, showError, userProfile, currentUser]);

  // Initial fetch - only when userId changes to avoid loops
  useEffect(() => {
    if (userId) {
      fetchProfile();
    } else {
      // No userId provided - not loading anything
      setLoading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh profile (bypass cache)
  const refreshProfile = useCallback(async () => {
    setRefreshing(true);
    const result = await fetchProfile(false);
    setRefreshing(false);
    return result;
  }, [fetchProfile]);

  // Update or create profile
  const updateProfile = useCallback(async (updates) => {
    try {
      let response;
      
      // If profile doesn't exist, create it instead of updating
      if (profileNotFound || !profile) {
        // Don't include userId in body - it's in the URL
        const createData = {
          ...updates
          // Remove userId from body as it's in the URL parameter
        };
        
        // Pass userId separately so service can use it in URL
        response = await playerProfileService.createProfile({
          ...createData,
          userId: userId  // This will be extracted by the service for URL
        });
        setProfileNotFound(false); // Profile now exists
      } else {
        response = await playerProfileService.updateProfile(userId, updates);
      }
      
      // Update local state
      setProfile(response.profile);
      
      // Clear cache for this profile
      for (const key of profileCache.keys()) {
        if (key.startsWith(userId)) {
          profileCache.delete(key);
        }
      }
      
      return response;
    } catch (err) {
      console.error('Error updating/creating profile:', err);
      showError(`Failed to ${profileNotFound ? 'create' : 'update'} profile: ${err.message}`);
      throw err;
    }
  }, [userId, profileNotFound, profile, showError]);

  // Check if a section is allowed based on privacy
  const isSectionAllowed = useCallback((section) => {
    // If in preview mode and owner, simulate viewer permissions
    if (isOwner && previewMode !== 'owner' && profile?.privacySettings) {
      const privacyLevel = profile.privacySettings[section] || 'private';
      
      // Public viewer can only see public sections
      if (previewMode === 'public') {
        return privacyLevel === 'public';
      }
      
      // Club member can see public and clubs sections
      if (previewMode === 'club') {
        return privacyLevel === 'public' || privacyLevel === 'clubs';
      }
    }
    
    // Default: use allowedSections from backend
    return allowedSections.includes(section);
  }, [allowedSections, isOwner, previewMode, profile]);

  // Get privacy level for a section
  const getPrivacyLevel = useCallback((section) => {
    return profile?.privacySettings?.[section] || 'private';
  }, [profile]);

  // Update privacy settings
  const updatePrivacy = useCallback(async (section, level) => {
    try {
      const response = await playerProfileService.updatePrivacySetting(userId, section, level);
      
      // Update local state
      if (profile) {
        setProfile({
          ...profile,
          privacySettings: response.privacySettings
        });
      }
      
      // Clear cache
      profileCache.delete(cacheKey);
      
      return response;
    } catch (err) {
      console.error('Error updating privacy:', err);
      showError(`Failed to update privacy settings: ${err.message}`);
      throw err;
    }
  }, [userId, profile, cacheKey, showError]);

  // Bulk update privacy
  const updatePrivacyBulk = useCallback(async (settings) => {
    try {
      const response = await playerProfileService.updatePrivacyBulk(userId, settings);
      
      // Update local state
      if (profile) {
        setProfile({
          ...profile,
          privacySettings: response.privacySettings
        });
      }
      
      // Clear cache
      for (const key of profileCache.keys()) {
        if (key.startsWith(userId)) {
          profileCache.delete(key);
        }
      }
      
      return response;
    } catch (err) {
      console.error('Error updating privacy bulk:', err);
      showError(`Failed to update privacy settings: ${err.message}`);
      throw err;
    }
  }, [userId, profile, showError]);

  // Track profile view (for non-owners)
  useEffect(() => {
    if (!loading && profile && !isOwner && !error) {
      // Track view after a short delay
      const timer = setTimeout(() => {
        playerProfileService.trackProfileView(profile._id, {
          sections: allowedSections,
          duration: 0, // Will be updated on unmount
          source: 'direct'
        }).catch(console.error);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [loading, profile, isOwner, error, allowedSections]);

  return {
    profile,
    loading,
    error,
    refreshing,
    isOwner,
    allowedSections,
    profileNotFound,
    ready: !userId || fetchedUserId === userId,
    
    // Methods
    refreshProfile,
    updateProfile,
    isSectionAllowed,
    getPrivacyLevel,
    updatePrivacy,
    updatePrivacyBulk,
    
    // Computed values
    profileCompleteness: profile?.profileCompleteness || 0,
    isVerified: profile?.verified || false,
    isFeatured: profile?.featured || false
  };
};
